import { ButtonSpinner } from "@/components/ButtonSpinner";
import { Loading } from "@/components/portal/Loading";
import { Button } from "@/components/ui/button";
import { Quill } from "@/crud/form/Quill/Quill";
import { BASE_URL } from "@/dashboardQueries";
import { useDarkMode } from "@/hooks";
import { AccreditationGroup } from "@/models/accreditation";
import { useAccreditationStore } from "@/store/accreditation";
import { PaperAirplaneIcon } from "@heroicons/react/24/outline";
import axios from "axios";
import { Field, Formik, FormikValues } from "formik";
import { Info, SendHorizonal, SendIcon, XIcon } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useMutation, useQuery } from "react-query";
import yup from "../../../../crud/yup-extended";

interface Props {
  group: AccreditationGroup;
}

export const SendEmail = ({ group }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { sendEmailOpen, setSendEmailOpen } = useAccreditationStore();
  const [sendEmailTo, setSendEmailTo] = useState<any | null>(null);
  const [isSending, setIsSending] = useState(false);
  const isDarkMode = useDarkMode();

  // Example templates
  const templates = [
    "Hi there,\n\nThank you for your email.",
    "Hello,\n\nYour tickets are attached.",
    "Dear recipient,\n\nPlease find the requested information below.",
  ];

  // State for template selection
  const [body, setBody] = useState("");
  const [templateIndex, setTemplateIndex] = useState<number | null>(null);

  // Handler for arrow key navigation
  const handleBodyKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
      let nextIndex = templateIndex ?? -1;
      if (e.key === "ArrowDown") {
        nextIndex = (nextIndex + 1) % templates.length;
      } else if (e.key === "ArrowUp") {
        nextIndex = (nextIndex - 1 + templates.length) % templates.length;
      }
      setTemplateIndex(nextIndex);
      setBody(templates[nextIndex]);
    }
  };

  const recipients = [
    {
      firstName: "Margot",
      lastName: "Foster",
      email: "margotfoster@mail.com",
    },
    {
      firstName: "John",
      lastName: "Doe",
      email: "johndoe@mail.com",
    },
    {
      firstName: "Jane",
      lastName: "Smith",
      email: "janesmith@mail.com",
    },
  ];

  // get emails
  const { data: emails, isLoading: emailsLoading } = useQuery(
    ["emails", group.id],
    async () => {
      const response = await axios.get(
        `${BASE_URL}/accreditation/groups/${group.id}/emails`
      );
      return response.data;
    }
  );

  // Send email
  const sendEmail = useMutation(
    async (email: FormikValues) => {
      const response = await axios.post(
        `${BASE_URL}/accreditation/groups/${group.id}/send-email`,
        {
          subject: email.subject,
          body: email.body,
          emails: sendEmailTo.map((email) => email.email),
        }
      );
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success("Email scheduled to send succesfully.");
        setIsSending(false);
        setSendEmailOpen(false);
      },
      onError: (e: any) => {
        toast.error("There was an error sending emails, please try again.");
        setIsSending(false);
      },
    }
  );

  // When we initially get emails, set the sendEmailTo to be all of them
  useEffect(() => {
    console.log(emails);
    if (emails && emails.emails && emails.emails.length > 0) {
      setSendEmailTo(emails.emails.map((email) => email));
    }
  }, [emails]);

  return (
    <AnimatePresence>
      {sendEmailOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-black/20 backdrop-blur-sm fixed inset-0 z-50 flex justify-center items-center"
        >
          <motion.div
            ref={containerRef}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="p-[1px] dark:bg-zinc-900 bg-gray-50 max-w-4xl w-full lg:-mt-44 rounded-lg border dark:border-white/10 shadow-lg"
          >
            {emails ? (
              <>
                <Formik
                  initialValues={{
                    subject: "",
                    body: "",
                  }}
                  onSubmit={async (values, { setSubmitting }) => {
                    // Handle form submission
                    setIsSending(true);
                    await sendEmail.mutateAsync(values);
                  }}
                  validationSchema={yup.object({
                    subject: yup.string().required("Subject is required"),
                    body: yup.string().required("Body is required"),
                  })}
                >
                  {({ handleSubmit, setFieldValue, errors }) => (
                    <form>
                      {/* attio look (add below): border border-white/10 rounded-md */}
                      <div className="flex justify-between items-center border-b dark:border-white/10 px-4 py-2">
                        <div>
                          <h4 className="text-sm dark:text-zinc-400">
                            Compose email
                          </h4>
                        </div>
                        <div>
                          <XIcon
                            className="inline size-6 cursor-pointer hover:bg-zinc-800 p-0.5 text-zinc-500 transition duration-200 rounded-lg"
                            onClick={() => setSendEmailOpen(false)}
                          />
                        </div>
                      </div>

                      {sendEmailTo && (
                        <div className="flex">
                          <div className="w-2/3 border-r dark:border-white/10">
                            <div className="">
                              <dl className="p-3 mt-1">
                                <div className="sm:grid grid-cols-5 mb-2">
                                  <dt className="dark:text-zinc-400/90 text-sm flex items-center">
                                    From
                                  </dt>
                                  <dd className="sm:col-span-4 flex items-center">
                                    <div className="mr-1.5 inline-flex items-center rounded-lg bg-gray-400/10 px-2 py-1 text-xs text-gray-400 ring-1 ring-inset ring-gray-400/20">
                                      <img
                                        src="https://ticketr.events/favicon-32x32.png"
                                        className="size-4 mr-1 rounded-full"
                                      />
                                      hello@ticketr.events
                                    </div>
                                    {/* todo: add tooltip to say if you want to change this, reach out to the account manager */}
                                    <Info className="inline size-4 dark:text-zinc-500" />
                                  </dd>
                                </div>
                                <div className="sm:grid grid-cols-5 mb-2">
                                  <dt className="dark:text-zinc-400/90 text-sm flex items-center">
                                    To
                                  </dt>
                                  <dd className="sm:col-span-4">
                                    <div className="mr-1.5 inline-flex items-center rounded-lg bg-gray-400/10 px-2 py-1 text-xs text-gray-400 ring-1 ring-inset ring-gray-400/20">
                                      <SendHorizonal className="inline size-3 mr-1" />
                                      {sendEmailTo?.length > 1
                                        ? `Sending individual emails to ${sendEmailTo.length} recipients`
                                        : `Sending email to ${sendEmailTo[0].email}`}
                                    </div>
                                  </dd>
                                </div>
                                <div className="sm:grid grid-cols-5">
                                  <dt className="dark:text-zinc-400/90 text-sm flex items-center">
                                    Subject
                                  </dt>
                                  <dd className="sm:col-span-4">
                                    <Field
                                      name="subject"
                                      type="text"
                                      placeholder="Enter subject..."
                                      className="bg-transparent p-0 border-0 focus:ring-0 focus:outline-none w-full"
                                    />
                                    {errors.subject && (
                                      <div className="text-xs text-red-400 mt-1">
                                        {errors.subject}
                                      </div>
                                    )}
                                  </dd>
                                </div>
                              </dl>
                            </div>
                            <div className="bg-green-500">
                              <div></div>
                            </div>
                            <div className="p-2">
                              <Quill name="body" />
                              {/* <Field
                              name="body"
                              component="textarea"
                              // onChange={(e) => {
                              //   // setBody(e.target.value);
                              //   // setTemplateIndex(null);
                              //   // setFieldValue("body", e.target.value);
                              // }}
                              // onKeyDown={handleBodyKeyDown}
                              placeholder="Type your message or use ↑/↓ to pick a template..."
                              className="m-3 bg-transparent p-0 border-0 focus:ring-0 focus:outline-none w-full min-h-[7rem]"
                            /> */}

                              <div className="bg-red-500 mt-4">
                                pick template
                              </div>
                            </div>
                          </div>
                          <div className="w-1/3 px-2">
                            <div className="">add recipients</div>
                            <div>
                              <ul>
                                {sendEmailTo.map((email, index) => (
                                  <li
                                    key={index}
                                    className="flex items-center mb-2.5 justify-between"
                                  >
                                    <div className="flex items-center w-full">
                                      <div className="dark:bg-zinc-800 py-1 px-1.5 rounded-full h-8 w-8 flex items-center justify-center">
                                        <span className="text-sm">
                                          {email.firstName
                                            .charAt(0)
                                            .toUpperCase() +
                                            email.lastName
                                              .charAt(0)
                                              .toUpperCase()}
                                        </span>
                                      </div>
                                      <div className="ml-2">
                                        <div className="text-sm -mb-0.5">
                                          {email.firstName} {email.lastName}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                          {email.email}
                                        </div>
                                      </div>
                                      <div className=" flex-1 justify-end flex">
                                        <XIcon
                                          className="inline size-4 cursor-pointer hover:bg-zinc-800 p-0.5 text-zinc-500 transition duration-200 rounded-md"
                                          onClick={() => {
                                            // remove this email from the list
                                            setSendEmailTo((prev) =>
                                              prev.filter(
                                                (e) => e.email !== email.email
                                              )
                                            );
                                          }}
                                        />
                                      </div>
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="sm:grid grid-cols-5">
                        <dd className="sm:col-span-4">
                          {/* <textarea
                        name="body"
                        value={body}
                        onChange={(e) => {
                          setBody(e.target.value);
                          setTemplateIndex(null);
                          setFieldValue("body", e.target.value);
                        }}
                        onKeyDown={handleBodyKeyDown}
                        placeholder="Type your message or use ↑/↓ to pick a template..."
                        className="bg-transparent p-0 border-0 focus:ring-0 focus:outline-none w-full min-h-[120px]"
                      /> */}
                          {/* <div className="text-xs text-zinc-400 mt-1">
                        Use ↑/↓ to cycle templates
                      </div> */}
                        </dd>
                      </div>
                      <div className="flex justify-between items-center p-2 border-t dark:border-white/10">
                        <div className="">
                          attachments, variables, email signature
                        </div>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleSubmit()}
                          type="button"
                        >
                          {isSending ? (
                            <>
                              <ButtonSpinner
                                colour={isDarkMode ? "black" : "white"}
                              />
                              Sending...
                            </>
                          ) : (
                            <>
                              Send email
                              <SendIcon className="size-4 ml-2" />
                            </>
                          )}
                        </Button>
                      </div>
                    </form>
                  )}
                </Formik>
              </>
            ) : (
              <Loading />
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
